import { create } from "zustand";
import { API_ENDPOINTS, apiFetchJson } from "@/lib/api";

export interface SystemMetrics {
    cpu_percent: number;
    memory_percent: number;
    disk_percent: number;
    load_average?: number[];
    network_io?: {
        bytes_sent: number;
        bytes_recv: number;
    };
}

export interface GamingMetrics {
    total_servers: number;
    online_servers: number;
    offline_servers: number;
    total_players: number;
    max_capacity: number;
    average_load: number;
    timestamp: string;
}

export interface DashboardMetrics {
    system: SystemMetrics;
    gaming: GamingMetrics;
}

interface MetricsStoreState {
    metrics: DashboardMetrics | null;
    isLoading: boolean;
    error: string | null;
}

interface MetricsStoreActions {
    fetchMetrics: () => Promise<void>;
    refresh: () => Promise<void>;
    startPolling: () => void;
    stopPolling: () => void;
}

let _pollRefCount = 0;
let _pollHandle: number | null = null;

export const useMetricsStore = create<MetricsStoreState & MetricsStoreActions>(
    (set, get) => ({
        metrics: null,
        isLoading: false,
        error: null,

        fetchMetrics: async () => {
            set({ isLoading: true, error: null });
            try {
                const [perf, overview] = await Promise.all([
                    apiFetchJson<any>(API_ENDPOINTS.SERVERS_PERFORMANCE),
                    apiFetchJson<any>(API_ENDPOINTS.SERVERS_OVERVIEW),
                ]);

                const system = perf?.system || {};
                const cpu = Number(system.cpu_usage ?? system.cpu ?? 0);
                const memory = Number(system.memory_usage ?? system.memory ?? 0);
                const disk = Number(system.disk_usage ?? system.disk ?? 0);
                const netSent = Number(system.network_io?.bytes_sent ?? 0);
                const netRecv = Number(system.network_io?.bytes_recv ?? 0);

                const summary = overview?.summary || {};

                set({
                    metrics: {
                        system: {
                            cpu_percent: Number.isFinite(cpu) ? cpu : 0,
                            memory_percent: Number.isFinite(memory) ? memory : 0,
                            disk_percent: Number.isFinite(disk) ? disk : 0,
                            network_io: {
                                bytes_sent: Number.isFinite(netSent) ? netSent : 0,
                                bytes_recv: Number.isFinite(netRecv) ? netRecv : 0,
                            },
                            load_average: Array.isArray(system.load_average)
                                ? system.load_average
                                : typeof system.load_average === "number"
                                  ? [system.load_average, 0, 0]
                                  : undefined,
                        },
                        gaming: {
                            total_servers: Number(summary.total_servers || 0),
                            online_servers: Number(summary.online_servers || 0),
                            offline_servers: Number(summary.offline_servers || 0),
                            total_players: Number(summary.total_players || 0),
                            max_capacity: Number(summary.max_capacity || 0),
                            average_load: Number(summary.average_load || 0),
                            timestamp: String(
                                overview?.timestamp || new Date().toISOString(),
                            ),
                        },
                    },
                    isLoading: false,
                });
            } catch (err) {
                const msg =
                    err instanceof Error ? err.message : "Failed to fetch metrics";
                console.error("Error fetching metrics:", err);
                set({ metrics: null, error: msg, isLoading: false });
            }
        },

        refresh: async () => get().fetchMetrics(),

        startPolling: () => {
            _pollRefCount += 1;
            if (_pollRefCount !== 1) return;

            void get().fetchMetrics();
            if (typeof window === "undefined") return;

            _pollHandle = window.setInterval(() => {
                void get().fetchMetrics();
            }, 30000);
        },

        stopPolling: () => {
            _pollRefCount = Math.max(0, _pollRefCount - 1);
            if (_pollRefCount !== 0) return;
            if (_pollHandle === null) return;

            window.clearInterval(_pollHandle);
            _pollHandle = null;
        },
    }),
);

